import type { CanvasDraw } from "@/4-components/animations/useCanvasLoop";
import { GREEN } from "@/5-shared/constants/palette";
import { rgba } from "@/5-shared/utils/color";

/**
 * The wireframe globe: orthographic lat/lon sphere with depth-faded strokes and
 * glowing front-face intersection dots. Shared by the bento impatto backdrop
 * (corner wash) and the about-page partner globe (centred subject), each passes
 * its own placement, tilt and rotation; the stroke/alpha maths is the identity.
 */
export type GlobeConfig = {
  /** Sphere centre as fractions of the canvas width/height. */
  center: [cx: number, cy: number];
  /** Sphere radius as a fraction of `min(w, h)`. */
  radius: number;
  /** Fixed X-axis tilt, radians. */
  tilt: number;
  /** Y rotation per frame, the seam where a consumer mixes in drag or scroll. */
  rotY: (t: number) => number;
  color?: string;
};

const MERIDIANS = 16;
const PARALLELS = 12;
const GLOBE_STEPS = 60;

/** Unit-sphere point → screen; `z` (front = +1) is the caller's depth cue. */
export function projectGlobe(lat: number, lon: number, rotY: number, rotX: number, radius: number, cx: number, cy: number) {
  const x = Math.cos(lat) * Math.sin(lon + rotY);
  const y = Math.sin(lat);
  const z = Math.cos(lat) * Math.cos(lon + rotY);
  const cosX = Math.cos(rotX);
  const sinX = Math.sin(rotX);
  const y2 = y * cosX - z * sinX;
  const z2 = y * sinX + z * cosX;
  return { x: cx + x * radius, y: cy - y2 * radius, z: z2 };
}

/** Build a `useCanvasLoop` draw that renders the wireframe sphere every frame. */
export function makeGlobeDraw({ center, radius, tilt, rotY, color = GREEN }: GlobeConfig): CanvasDraw {
  return (ctx, w, h, t) => {
    const cx = w * center[0];
    const cy = h * center[1];
    const r = Math.min(w, h) * radius;
    const ry = rotY(t);
    ctx.lineCap = "round";

    for (let m = 0; m < MERIDIANS; m++) {
      const lon = (m / MERIDIANS) * Math.PI * 2;
      ctx.beginPath();
      for (let i = 0; i <= GLOBE_STEPS; i++) {
        const lat = -Math.PI / 2 + (i / GLOBE_STEPS) * Math.PI;
        const p = projectGlobe(lat, lon, ry, tilt, r, cx, cy);
        if (i === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      }
      const mid = projectGlobe(0, lon, ry, tilt, r, cx, cy);
      const a = 0.04 + Math.max(0, mid.z) * 0.16;
      ctx.strokeStyle = rgba(color, a);
      ctx.lineWidth = 1 + Math.max(0, mid.z) * 0.8;
      ctx.stroke();
    }

    // one ring of latitude, traced but not stroked, the caller picks the weight
    const parallelPath = (lat: number) => {
      ctx.beginPath();
      for (let i = 0; i <= GLOBE_STEPS; i++) {
        const lon = (i / GLOBE_STEPS) * Math.PI * 2;
        const pt = projectGlobe(lat, lon, ry, tilt, r, cx, cy);
        if (i === 0) ctx.moveTo(pt.x, pt.y);
        else ctx.lineTo(pt.x, pt.y);
      }
    };

    for (let p = 1; p < PARALLELS; p++) {
      const lat = -Math.PI / 2 + (p / PARALLELS) * Math.PI;
      parallelPath(lat);
      ctx.strokeStyle = rgba(color, 0.04 + Math.max(0, Math.cos(lat)) * 0.1);
      ctx.lineWidth = 0.8 + Math.max(0, Math.cos(lat)) * 0.5;
      ctx.stroke();
    }

    // a slightly bolder equator
    parallelPath(0);
    ctx.strokeStyle = rgba(color, 0.16);
    ctx.lineWidth = 1.8;
    ctx.stroke();

    // glowing dots at intersections on the front face
    for (let m = 0; m < MERIDIANS; m += 2) {
      const lon = (m / MERIDIANS) * Math.PI * 2;
      for (let p = 1; p < PARALLELS; p += 2) {
        const lat = -Math.PI / 2 + (p / PARALLELS) * Math.PI;
        const pt = projectGlobe(lat, lon, ry, tilt, r, cx, cy);
        if (pt.z > 0.15) {
          ctx.beginPath();
          ctx.arc(pt.x, pt.y, 1.5 + pt.z * 1.5, 0, Math.PI * 2);
          ctx.fillStyle = rgba(color, pt.z * 0.3);
          ctx.fill();
        }
      }
    }
  };
}
